import { Routes, Route, Navigate, NavLink, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Substances from "@/pages/Substances";
import MRIScans from "@/pages/MRIScans";
import Findings from "@/pages/Findings";
import AccessRequests from "@/pages/AccessRequests";
import { Button } from "@/components/ui/button";
import { FlaskConical, Brain, FileSearch, LogOut, Loader2, Users } from "lucide-react";

const NAV = [
  { to: "/substances", label: "Substances", icon: FlaskConical },
  { to: "/mri-scans", label: "MRI Scans", icon: Brain },
  { to: "/findings", label: "Research Findings", icon: FileSearch },
];

function Shell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const nav = user?.role === "admin"
    ? [...NAV, { to: "/access-requests", label: "Access Requests", icon: Users }]
    : NAV;
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border sticky top-0 z-20 bg-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-6">
          <div className="font-heading font-semibold text-sm tracking-tight whitespace-nowrap">Conscious Basel</div>
          <nav className="flex items-center gap-1 overflow-x-auto">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors duration-200 whitespace-nowrap ${
                    isActive ? "bg-foreground text-background" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`
                }
              >
                <n.icon className="h-4 w-4" />
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex-1" />
          <span className="text-xs text-muted-foreground hidden sm:block" data-testid="current-user">{user?.name || user?.email}</span>
          <Button variant="ghost" size="sm" onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-btn">
            <LogOut className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Sign out</span>
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <Routes>
          <Route path="/" element={<Navigate to="/substances" replace />} />
          <Route path="/substances" element={<Substances />} />
          <Route path="/mri-scans" element={<MRIScans />} />
          <Route path="/findings" element={<Findings />} />
          {user?.role === "admin" && <Route path="/access-requests" element={<AccessRequests />} />}
          <Route path="*" element={<Navigate to="/substances" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export default function App() {
  const { user, checking } = useAuth();

  if (checking || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      {!user ? (
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      ) : (
        <Routes>
          <Route path="/login" element={<Navigate to="/substances" replace />} />
          <Route path="/*" element={<Shell />} />
        </Routes>
      )}
      <Toaster position="top-right" richColors />
    </>
  );
}
